import { getProductUrl, getTranslatedProperty } from '@shopware/helpers'
import { sortBy } from 'lodash-es'
import type { Breadcrumb } from '@shopware/composables'
import type { Schemas } from '#shopware'

export interface ListElement {
	name?: string
	url?: string
	item?: string | Thing
}

interface Thing {
	'@type'?: string
	'@id'?: string
	name?: string
}

interface useSchemaOrgJsonReturn {
	defineProduct: (product: Schemas['Product']) => void
	defineOrganization: (phoneNumber: string) => void
	defineBreadcrumbs: (breadcrumbs: Ref<Breadcrumb[]>) => void
	defineWebsite: () => void
	defineItemList: (products: Schemas['Product'][]) => void
}

export const useSchemaOrgJson = (): useSchemaOrgJsonReturn => {
	const config = useRuntimeConfig()
	const { t } = useI18n()

	const _defineProduct = (product: Schemas['Product']): void => {
		const { currency } = useSessionContext()

		const productName = getTranslatedProperty(product, 'name')
		const productDescription = getTranslatedProperty(product, 'description').replace(/<\/?[^>]+>/gi, ' ')
		const manufacturerName = getTranslatedProperty(product.manufacturer, 'name')
        const isInStock = computed(
            () => product?.availableStock && product?.availableStock > 0
        );          
		const { unitPrice } = useProductPrice(computed(() => product))
		const media = computed(() => sortBy(product.media, 'position'))

		useSchemaOrg([
			defineProduct({
				name: productName,
				brand: manufacturerName,
				description: productDescription,
				sku: product.productNumber,
				image: media.value[0]?.media?.url,
				...(unitPrice?.value > 0
					? {
						offers: {
							price: unitPrice,
							availability: isInStock
								? 'https://schema.org/OutOfStock'
								: 'https://schema.org/InStock',
							priceCurrency: currency?.value?.shortName || 'EUR',
							itemCondition: 'http://schema.org/NewCondition'
						}
					  }
					: {})
			})
		])
	}

	const _defineItemList = (products: Schemas['Product'][]): void => {
		const listItems = products.map((product): any => {
			return { name: getTranslatedProperty(product, 'name'), url: getProductUrl(product) }
		})

		useSchemaOrg(
			defineItemList({
				itemListElement: listItems
			})
		)
	}

	const _defineBreadcrumbs = (breadcrumbs: Ref<Breadcrumb[]>): void => {
		const listElements = breadcrumbs.value.map((breadcrumb): ListElement => {
			return {
				item: {
					'@type': 'Thing',
					'@id': breadcrumb?.path ? config.public.shopware.devStorefrontUrl + '/' + breadcrumb.path : '',
					name: breadcrumb.name
				}
			}
		})

		useSchemaOrg([
			defineBreadcrumb({
				itemListElement: [
					{ item: { '@type': 'Thing', '@id': config.public.shopware.devStorefrontUrl, name: t('Home') } },
					...listElements
				]
			})
		])
	}

	const _defineOrganization = (phoneNumber: string): void => {
		useSchemaOrg([
			defineOrganization({
				url: config.public.shopware.devStorefrontUrl,
				logo: '/assets/logo.png',
				name: 'Kavos bankas',
				contactPoint: [
					{
						'@type': 'ContactPoint',
						telephone: phoneNumber,
						contactType: 'customer service'
					}
				]
			})
		])
	}

	const _defineWebsite = (): void => {
		useSchemaOrg([
			defineWebSite({
				url: config.public.shopware.devStorefrontUrl,
				potentialAction: {
					target: `/search?q={search_term_string}`
				},
				'query-input': {
					valueRequired: true,
					'@type': 'PropertyValueSpecification'
				}
			})
		])
	}

	return {
		defineProduct: _defineProduct,
		defineOrganization: _defineOrganization,
		defineBreadcrumbs: _defineBreadcrumbs,
		defineWebsite: _defineWebsite,
		defineItemList: _defineItemList
	}
}
